// src/features/admin-options/components/EventNotificationConfigHub.tsx

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  NotificationsActive as NotificationsIcon,
  CopyAll as CopyAllIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider, useGetList } from 'react-admin';
import { getCurrentUser } from '@/utils/auth';
import {
  EventNotificationConfigForm,
  type EventNotificationConfig,
} from './EventNotificationConfigForm';

const DEFAULT_CONFIG: EventNotificationConfig = {
  enabled: false,
  recipients: [],
  preferences: {
    notifyKeyDispensed: false,
    notifyCardLow: true,
    notifyCardEmpty: true,
    notifyCheckInCompleted: true,
    notifyPreCheckInCompleted: true,
    notifyCriticalErrors: true,
  },
};

interface HotelOption {
  id: string;
  name: string;
  groupName?: string;
  groupId?: string;
}

export const EventNotificationConfigHub: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider() as any;
  const t = (key: string) => translate(`resources.adminOptions.eventNotifications.${key}`);

  const user = getCurrentUser();
  const isGroupAdmin = user?.role === 'GroupAdmin';

  // Load hotels
  const hotelFilter = isGroupAdmin && user?.assignedGroups?.length
    ? { groupId: user.assignedGroups }
    : {};

  const { data: hotels = [], isLoading: hotelsLoading } = useGetList<any>('hotels', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' as const },
    filter: hotelFilter,
  });

  const hotelOptions: HotelOption[] = hotels.map((h: any) => ({
    id: h.id,
    name: h.name,
    groupName: h.groupName || h.group?.name || '',
    groupId: h.groupId,
  }));

  // State
  const [selectedHotel, setSelectedHotel] = useState<HotelOption | null>(null);

  // Load devices for selected hotel to determine which notification sections to show
  const { data: hotelDevices = [] } = useGetList<any>('devices', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'installationCode', order: 'ASC' as const },
    filter: selectedHotel ? { hotelId: selectedHotel.id } : {},
  }, { enabled: !!selectedHotel });

  const hotelDeviceTypes = [...new Set(hotelDevices.map((d: any) => d.deviceType).filter(Boolean))];

  const [config, setConfig] = useState<EventNotificationConfig>(DEFAULT_CONFIG);
  const [savedConfig, setSavedConfig] = useState<EventNotificationConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const isDirty = JSON.stringify(config) !== JSON.stringify(savedConfig);
  const initialLoad = useRef(true);

  // Load config when hotel changes
  const loadConfig = useCallback(async (hotelId: string) => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getEventNotificationConfig(hotelId);
      if (data) {
        setConfig(data);
        setSavedConfig(data);
      } else {
        setConfig(DEFAULT_CONFIG);
        setSavedConfig(DEFAULT_CONFIG);
      }
    } catch {
      setConfig(DEFAULT_CONFIG);
      setSavedConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [dataProvider]);

  useEffect(() => {
    if (selectedHotel) {
      loadConfig(selectedHotel.id);
    }
  }, [selectedHotel, loadConfig]);

  // Auto-select first hotel
  useEffect(() => {
    if (initialLoad.current && hotelOptions.length > 0 && !selectedHotel) {
      setSelectedHotel(hotelOptions[0]);
      initialLoad.current = false;
    }
  }, [hotelOptions, selectedHotel]);

  const handleHotelChange = (_: any, value: HotelOption | null) => {
    if (isDirty) {
      if (!window.confirm(t('messages.unsavedChanges'))) return;
    }
    setSelectedHotel(value);
  };

  const handleSave = async () => {
    if (!selectedHotel) return;
    setSaving(true);
    try {
      const { data } = await dataProvider.updateEventNotificationConfig(selectedHotel.id, {
        enabled: config.enabled,
        recipients: config.recipients,
        preferences: config.preferences,
      });
      setConfig(data);
      setSavedConfig(data);
      notify(t('messages.saveSuccess'), { type: 'success' });
    } catch {
      notify(t('messages.saveError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!selectedHotel) return;
    setTesting(true);
    try {
      await dataProvider.sendTestEventNotification(selectedHotel.id);
      notify(t('messages.testSuccess'), { type: 'success' });
    } catch {
      notify(t('messages.testError'), { type: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const handleApplyToGroup = async () => {
    if (!selectedHotel?.groupId) return;
    try {
      await dataProvider.applyEventNotificationConfigToGroup(
        selectedHotel.groupId,
        selectedHotel.id
      );
      notify(t('messages.applySuccess'), { type: 'success' });
    } catch {
      notify(t('messages.applyError'), { type: 'error' });
    } finally {
      setApplyDialogOpen(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <NotificationsIcon color="primary" />
        <Typography variant="h6">{t('title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('subtitle')}
      </Typography>

      {/* Hotel selector */}
      {hotelsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : hotelOptions.length === 0 ? (
        <Alert severity="info">{t('noHotels')}</Alert>
      ) : (
        <>
          <Autocomplete
            value={selectedHotel}
            onChange={handleHotelChange}
            options={hotelOptions}
            getOptionLabel={(opt) => opt.name}
            groupBy={(opt) => opt.groupName || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('selectHotel')}
                helperText={t('selectHotelHelper')}
                size="small"
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Config form */}
          {selectedHotel && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <EventNotificationConfigForm
                    config={config}
                    onChange={setConfig}
                    onSave={handleSave}
                    onTest={handleTest}
                    saving={saving}
                    testing={testing}
                    dirty={isDirty}
                    hotelDeviceTypes={hotelDeviceTypes}
                  />

                  {/* Apply to group */}
                  {selectedHotel.groupId && config.enabled && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CopyAllIcon />}
                        onClick={() => setApplyDialogOpen(true)}
                        disabled={isDirty}
                      >
                        {t('actions.applyToGroup')}
                      </Button>
                      {isDirty && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {t('messages.unsavedChanges')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Confirm apply dialog */}
      <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)}>
        <DialogTitle>{t('actions.applyToGroup')}</DialogTitle>
        <DialogContent>
          <Typography>{t('messages.applyConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>
            {translate('ra.action.cancel')}
          </Button>
          <Button variant="contained" onClick={handleApplyToGroup}>
            {translate('ra.action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
